/**
 * Router Configuration
 *
 * Defines the application route structure with authentication
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';

import App from '../App';
import { AdminRoute } from '../components/auth/AdminRoute';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { AboutPage } from '../pages/About/AboutPage';
import AdminDashboardPage from '../pages/Admin/AdminDashboardPage';
import ScoresPage from '../pages/Admin/ScoresPage';
import { LoginPage } from '../pages/auth/LoginPage';
import { HomePage } from '../pages/Home/HomePage';
import { QueuePage } from '../pages/Home/QueuePage';
import { InvestmentDetailPage } from '../pages/InvestmentEditor/InvestmentDetailPage';
import { InvestmentEditorPage } from '../pages/InvestmentEditor/InvestmentEditorPage';
import { NewInvestmentPage } from '../pages/InvestmentEditor/NewInvestmentPage';
import { RoadmapListPage, RoadmapViewPage } from '../pages/Roadmap';

// Placeholder components for routes
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-gray-900">{title}</h1>
      <p className="mt-4 text-gray-600">This page is under construction.</p>
    </div>
  </div>
);

export const router = createBrowserRouter(
  [
    // Login route (public)
    {
      path: '/login',
      element: <LoginPage />,
    },
    // Protected routes
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <App />
        </ProtectedRoute>
      ),
      children: [
        // Main routes
        {
          index: true,
          element: <HomePage />,
        },
        {
          path: 'investments',
          children: [
            {
              index: true,
              element: <Navigate to="/" replace />,
            },
            {
              path: 'new',
              element: <NewInvestmentPage />,
            },
            {
              path: ':id',
              element: <InvestmentDetailPage />,
            },
            {
              path: ':id/edit',
              element: <InvestmentEditorPage />,
            },
          ],
        },
        {
          path: 'queue',
          element: <QueuePage />,
        },
        {
          path: 'roadmap',
          children: [
            {
              index: true,
              element: <RoadmapListPage />,
            },
            {
              path: ':id',
              element: <RoadmapViewPage />,
            },
          ],
        },
        {
          path: 'about',
          element: <AboutPage />,
        },

        // Admin routes (requires Administrator role)
        {
          path: 'admin',
          element: (
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          ),
        },
        {
          path: 'admin/scores',
          element: (
            <AdminRoute>
              <ScoresPage />
            </AdminRoute>
          ),
        },

        // Error routes
        {
          path: '*',
          element: <PlaceholderPage title="404 - Page Not Found" />,
        },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

export default router;
